<script>
import { sanitizeHtml } from '@frontend/utils/sanitizeHtml';
import PreviewFileModal from '@frontend/components/common/PreviewFileModal.vue';
import {
  downloadUrlAsFile,
  fullFileNameFromUrl,
} from '@frontend/components/briefs/briefReferenceUtils';

const SEARCH_ICON_SVG = '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>';
const DOWNLOAD_ICON_SVG = '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" x2="12" y1="15" y2="3"/></svg>';

// Render function (not v-html) so sanitization is mandatory and vue/no-v-html never fires.
export default {
  name: 'RichTextDisplay',
  components: { PreviewFileModal },
  props: {
    html: {
      type: String,
      default: '',
    },
    // Sidebar briefs: magnify + download on hover (same pattern as layout-page image-preview).
    showImageActions: {
      type: Boolean,
      default: false,
    },
  },
  mounted() {
    this.enhanceImages();
  },
  updated() {
    this.enhanceImages();
  },
  methods: {
    // Default: click image to preview. With showImageActions, use the magnify button instead.
    onContentClick(event) {
      if (this.showImageActions) return;
      const target = event && event.target;
      if (!target || target.tagName !== 'IMG' || !this.$refs.preview) {
        return;
      }
      this.$refs.preview.show(target.currentSrc || target.src, 'image');
    },
    openPreview(src) {
      if (this.$refs.preview && src) {
        this.$refs.preview.show(src, 'image');
      }
    },
    async downloadImage(src) {
      if (!src) return;
      const filename = fullFileNameFromUrl(src) || 'image';
      await downloadUrlAsFile(src, filename);
    },
    enhanceImages() {
      const root = this.$el && this.$el.querySelector('.rich-text-display');
      if (!root || !this.showImageActions) return;

      root.querySelectorAll('img').forEach((img) => {
        if (img.closest('.rich-text-display__media')) return;

        const wrap = document.createElement('span');
        wrap.className = 'rich-text-display__media';
        img.parentNode.insertBefore(wrap, img);
        wrap.appendChild(img);

        const actions = document.createElement('div');
        actions.className = 'rich-text-display__actions';

        const magnify = document.createElement('button');
        magnify.type = 'button';
        magnify.className = 'rich-text-display__action';
        magnify.setAttribute('aria-label', 'View larger');
        magnify.title = 'View larger';
        magnify.innerHTML = SEARCH_ICON_SVG;
        magnify.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.openPreview(img.currentSrc || img.src);
        });

        const download = document.createElement('button');
        download.type = 'button';
        download.className = 'rich-text-display__action';
        download.setAttribute('aria-label', 'Download image');
        download.title = 'Download';
        download.innerHTML = DOWNLOAD_ICON_SVG;
        download.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.downloadImage(img.currentSrc || img.src);
        });

        actions.appendChild(magnify);
        actions.appendChild(download);
        wrap.appendChild(actions);
      });
    },
  },
  render(h) {
    const clean = sanitizeHtml(this.html);
    const children = [
      h('div', {
        class: 'rich-text-display',
        domProps: { innerHTML: clean },
        on: { click: this.onContentClick },
      }),
    ];
    // Mounted only when there's an image, so text-only comment displays don't each add a hidden modal.
    if (clean.includes('<img')) {
      children.push(h(PreviewFileModal, { ref: 'preview' }));
    }
    return h('div', { class: 'rich-text-display-wrapper' }, children);
  },
};
</script>

<style scoped>
.rich-text-display {
  white-space: normal;
  word-break: break-word;
  counter-reset: list-0;
}

.rich-text-display ::v-deep p {
  margin: 0 0 0.5rem;
}

.rich-text-display ::v-deep p:last-child {
  margin-bottom: 0;
}

.rich-text-display ::v-deep ol,
.rich-text-display ::v-deep ul {
  margin: 0.5rem 0;
  padding-left: 1.5rem;
}

/* Quill 2: both bullets and ordered lists are <ol><li data-list="...">. */
.rich-text-display ::v-deep li {
  position: relative;
  list-style-type: none;
  padding-left: 1.5rem;
  margin: 0.25rem 0;
}

/* Runtime Quill inserts .ql-ui; saved HTML often omits it — mark via li::before either way. */
.rich-text-display ::v-deep li > .ql-ui {
  display: none;
}

.rich-text-display ::v-deep li[data-list='bullet']::before {
  content: '\2022';
  position: absolute;
  left: 0;
  width: 1.2em;
  text-align: right;
}

.rich-text-display ::v-deep li[data-list='ordered'] {
  counter-increment: list-0;
}

.rich-text-display ::v-deep li[data-list='ordered']::before {
  content: counter(list-0, decimal) '. ';
  position: absolute;
  left: 0;
  width: 1.2em;
  text-align: right;
  white-space: nowrap;
}

/* Fallback when data-list was stripped: show native markers. */
.rich-text-display ::v-deep ol > li:not([data-list]) {
  list-style-type: decimal;
  padding-left: 0;
}

.rich-text-display ::v-deep ul > li:not([data-list]) {
  list-style-type: disc;
  padding-left: 0;
}

.rich-text-display ::v-deep a {
  color: hsl(162, 73%, 35%);
  text-decoration: underline;
}

.rich-text-display ::v-deep img {
  max-width: 100%;
  max-height: 240px;
  border-radius: calc(0.375rem - 2px);
  cursor: zoom-in;
}

.rich-text-display ::v-deep .rich-text-display__media {
  position: relative;
  display: inline-block;
  max-width: 100%;
  margin: 0.35rem 0;
  vertical-align: middle;
  overflow: hidden;
  border-radius: calc(0.375rem - 2px);
}

.rich-text-display ::v-deep .rich-text-display__media img {
  display: block;
  cursor: default;
}

.rich-text-display ::v-deep .rich-text-display__actions {
  position: absolute;
  top: 0.35rem;
  right: 0.35rem;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  opacity: 0;
  transition: opacity 0.15s ease;
}

.rich-text-display ::v-deep .rich-text-display__action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  padding: 0;
  border: none;
  border-radius: 9999px;
  background: hsl(0 0% 100% / 0.92);
  color: hsl(222.2, 47.4%, 11.2%);
  box-shadow: 0 1px 2px hsl(0 0% 0% / 0.08);
  cursor: pointer;
  line-height: 1;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.rich-text-display ::v-deep .rich-text-display__action:hover {
  background: hsl(0 0% 100%);
  color: #189a73;
}

.rich-text-display ::v-deep .rich-text-display__media:hover .rich-text-display__actions {
  opacity: 1;
}

.rich-text-display ::v-deep video {
  max-width: 100%;
  max-height: 240px;
  border-radius: 4px;
}

.rich-text-display ::v-deep .brief-image-type-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 1px 8px;
  margin: 2px 4px 2px 0;
  border-radius: 9999px;
  background: hsl(162 73% 35% / 0.12);
  color: hsl(162 73% 25%);
  border: 1px solid hsl(162 73% 35% / 0.35);
  font-size: 11px;
  font-weight: 600;
  line-height: 18px;
  vertical-align: middle;
  white-space: nowrap;
}
</style>
